import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Truck, Shield, User, Mail, Phone, MapPin, Check, Loader2, ExternalLink } from 'lucide-react';
import styled from 'styled-components';
import PaymentMethod from './PaymentMethod';
import { paymentService } from '../services/api';
import { validationService } from '../services/validation';
import { notificationService } from '../services/notification';
import productService from '../services/productService';
import authService from '../services/authService';

const Section = styled.section`
  min-height: 100vh;
  background: #f8fafc;
  padding: 6rem 1rem 2rem;
  
  @media (min-width: 768px) {
    padding: 6rem 2rem 2rem;
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: clamp(1.5rem, 4vw, 2rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #6b7280;
    font-size: 1rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 1rem;
  transition: color 0.2s ease;
  
  &:hover {
    color: #3b82f6;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 6rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }
  
  input, select {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ProductItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
  
  .product-image {
    width: 60px;
    height: 60px;
    border-radius: 0.5rem;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }
  
  .product-details {
    flex: 1;
    
    h4 {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 0.25rem;
    }
    
    .config {
      font-size: 0.75rem;
      color: #6b7280;
      margin-bottom: 0.25rem;
    }
    
    .quantity {
      font-size: 0.75rem;
      color: #6b7280;
    }
  }
  
  .product-price {
    font-weight: 600;
    color: #1f2937;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  
  &.total {
    border-top: 2px solid #e5e7eb;
    margin-top: 1rem;
    padding-top: 1rem;
    font-weight: 700;
    font-size: 1.125rem;
    color: #1f2937;
  }
`;


const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const FeatureBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 9999px;
  font-size: 0.75rem;
  color: #166534;
  margin-bottom: 0.5rem;
`;

const CheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    paymentMethod: 'wave',
    deliveryOption: 'pickup' // pickup ou delivery
  });
  
  const [orderItems, setOrderItems] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  useEffect(() => {
    // Récupérer le profil utilisateur si connecté
    const fetchUserProfile = async () => {
      try {
        const profile = await authService.getUserProfile();
        console.log('Profil utilisateur récupéré:', profile);
        setUserProfile(profile);
        
        // Pré-remplir le formulaire avec les données de l'utilisateur
        if (profile && profile.user) {
          console.log('Structure du profil:', Object.keys(profile));
          console.log('Valeurs du profil:', profile);
          console.log('Données utilisateur:', profile.user);
          
          const userData = profile.user;
          setFormData(prev => ({
            ...prev,
            firstName: userData.first_name || '',
            lastName: userData.last_name || '',
            email: userData.email || '',
            phone: userData.phone || '',  // Si le champ phone existe dans le modèle
            // Les autres champs restent vides pour que l'utilisateur les remplisse
          }));
        }
      } catch (error) {
        console.log('Utilisateur non connecté ou erreur profil:', error);
        // L'utilisateur n'est pas connecté, on continue avec le formulaire vide
      }
    };
    
    fetchUserProfile();
  }, []);

  useEffect(() => {
    const fetchOrderItems = async () => {
      // Parse URL parameters to get cart items
      console.log('=== Parsing URL parameters ===');
      console.log('URL complète:', window.location.href);
      console.log('searchParams:', Object.fromEntries(searchParams.entries()));
      
      const items = [];
      
      // Vérifier si c'est le format d'un seul produit (depuis ProductPage)
      if (searchParams.has('product') && !searchParams.has('product_0')) {
        const productId = searchParams.get('product');
        const storage = searchParams.get('storage') || '128GB';
        const color = searchParams.get('color') || 'Noir';
        const quantity = parseInt(searchParams.get('quantity')) || 1;
        
        console.log(`Format produit unique: ${productId}, ${storage}, ${color}, ${quantity}`);
        
        try {
          const productData = await productService.getProductById(productId);
          
          items.push({
            ...productData,
            quantity,
            storage,
            color,
            totalPrice: productData.price * quantity
          });
        } catch (error) {
          console.error(`Erreur lors du chargement du produit ${productId}:`, error);
        }
      } else {
        // Format multiple articles (depuis CartPage)
        let index = 0;
        
        while (searchParams.has(`product_${index}`)) {
          const productId = searchParams.get(`product_${index}`);
          const storage = searchParams.get(`storage_${index}`) || '128GB';
          const color = searchParams.get(`color_${index}`) || 'Noir';
          const quantity = parseInt(searchParams.get(`quantity_${index}`)) || 1;
          
          try {
            // Fetch product data from API
            const productData = await productService.getProductById(productId);
            
            console.log(`Article ${index}:`, {
              productId,
              storage,
              color,
              quantity,
              productData
            });
            
            items.push({
              ...productData,
              quantity,
              storage,
              color,
              totalPrice: productData.price * quantity
            });
          } catch (error) {
            console.error(`Erreur lors du chargement du produit ${productId}:`, error);
            // Continuer avec les autres produits même si celui-ci échoue
          }
          
          index++;
        }
      }
      
      console.log('Articles finaux:', items);
      setOrderItems(items);
    };
    
    fetchOrderItems();
  }, [searchParams]);

  const handleInputChange = (e) => {
    console.log('handleInputChange called:', e.target.name, e.target.value);
    console.log('Current formData:', formData);
    setFormData(prev => {
      console.log('Setting new state:', {...prev, [e.target.name]: e.target.value});
      return {
        ...prev,
        [e.target.name]: e.target.value
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Valider les informations client
      const customerInfo = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        postalCode: formData.postalCode
      };
      
      const validation = validationService.validateCustomerInfo(customerInfo);
      if (!validation.valid) {
        notificationService.showError(validation.errors.join(', '));
        setIsSubmitting(false);
        return;
      }
      
      // Préparer les données de commande
      console.log('=== Construction orderData ===');
      console.log('calculateTotal():', calculateTotal());
      console.log('calculateShipping():', calculateShipping());
      console.log('validation:', validation);
      console.log('customerInfo:', customerInfo);
      console.log('orderItems:', orderItems);
      
      const orderData = {
        orderId: `ORD-${Date.now()}`,
        amount: calculateTotal() + calculateShipping(),
        phone: validation.normalizedPhone,
        customerInfo: {
          ...customerInfo,
          phone: validation.normalizedPhone
        },
        items: orderItems.map(item => ({
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.price,
          totalPrice: item.totalPrice,
          storage: item.storage,
          color: item.color
        })),
        deliveryAddress: `${formData.address}, ${formData.city}, ${formData.postalCode}`
      };

      if (formData.paymentMethod === 'wave') {
        // Paiement Wave avec notifications
        notificationService.showInfo('Initialisation du paiement Wave...');
        
        console.log('=== Paiement Wave ===');
        console.log('formData:', formData);
        console.log('orderData:', orderData);
        
        const paymentResponse = await paymentService.initiateWavePayment(orderData);
        
        if (paymentResponse.success) {
          notificationService.showSuccess('Paiement Wave initialisé! Redirection en cours...');
          
          // Rediriger vers Wave
          if (paymentResponse.payment_url) {
            // Optionnel: Polling du statut de paiement
            setTimeout(() => {
              window.location.href = paymentResponse.payment_url;
            }, 1000);
          } else {
            // Afficher QR code et instructions
            showWaveInstructions(paymentResponse);
          }
        } else {
          notificationService.showError('Erreur lors de l\'initialisation du paiement Wave');
        }
      } else if (formData.paymentMethod === 'cash_on_delivery') {
        // Paiement après livraison
        notificationService.showInfo('Confirmation de votre commande...');
        
        const orderResponse = await paymentService.confirmCashOnDelivery(orderData);
        
        if (orderResponse.success) {
          notificationService.showSuccess('Commande confirmée! Vous recevrez une confirmation par SMS et email.');
          
          // Rediriger vers la page de confirmation
          setTimeout(() => {
            navigate('/order-confirmation', { 
              state: { 
                orderData: orderResponse,
                message: 'Votre commande a été confirmée avec succès!'
              }
            });
          }, 2000);
        } else {
          notificationService.showError('Erreur lors de la confirmation de la commande');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      notificationService.showError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showWaveInstructions = (paymentResponse) => {
    // Afficher les instructions de paiement Wave
    const instructions = `
      <div style="text-align: center; padding: 2rem;">
        <h3>Paiement Wave</h3>
        <p>Scannez ce QR code avec l'application Wave</p>
        <img src="${paymentResponse.qr_code}" alt="QR Code Wave" style="max-width: 200px;" />
        <p>Ou cliquez sur ce lien:</p>
        <a href="${paymentResponse.payment_url}" target="_blank">Payer avec Wave</a>
        <p>Montant: ${paymentResponse.amount} XOF</p>
        <p>Expire: ${new Date(paymentResponse.expires_at).toLocaleString()}</p>
      </div>
    `;
    
    // Créer une modal pour afficher les instructions
    const modal = document.createElement('div');
    modal.innerHTML = instructions;
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;
    
    document.body.appendChild(modal);
    
    // Fermer la modal en cliquant à l'extérieur
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  };

  // Calcul des totaux
  const calculateSubtotal = () => {
    return orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateShipping = () => {
    // Frais de port uniquement si livraison à domicile
    return formData.deliveryOption === 'delivery' ? 2500 : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateShipping();
  };

  return (
  <Section>
    <Container>
      <BackButton onClick={() => navigate('/boutique')}>
        <ArrowLeft size={16} />
        Retour à la boutique
      </BackButton>
        
      <Header>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Finaliser la commande</h1>
          <p>Veuillez remplir vos informations pour compléter votre commande</p>
        </motion.div>
      </Header>

      <CheckoutGrid>
        <FormSection>
          <motion.form onSubmit={handleSubmit}>
            <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>
              Informations de livraison
            </h2>
              
            <FormRow>
              <FormGroup>
                <label>Prénom</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
                
              <FormGroup>
                <label>Nom</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </FormRow>
              
            <FormGroup>
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
              
            <FormGroup>
              <label>Téléphone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleInputChange}
                placeholder="Ex: 07XX XX XX XX ou 05XX XX XX XX"
                required
                autoComplete="tel"
                style={{pointerEvents: 'auto', userSelect: 'text'}}
              />
            </FormGroup>
              
            <FormGroup>
              <label>Adresse</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </FormGroup>
              
            <FormRow>
              <FormGroup>
                <label>Ville</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
                
              <FormGroup>
                <label>Code postal</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
            </FormRow>

            {/* Option de livraison */}
            <FormGroup>
              <label>Option de livraison</label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="deliveryOption"
                    value="pickup"
                    checked={formData.deliveryOption === 'pickup'}
                    onChange={handleInputChange}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Retrait en magasin (Gratuit)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="deliveryOption"
                    value="delivery"
                    checked={formData.deliveryOption === 'delivery'}
                    onChange={handleInputChange}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Livraison à domicile (2 500 FCFA)
                </label>
              </div>
            </FormGroup>

            {/* Champs d'adresse uniquement si livraison */}
            {formData.deliveryOption === 'delivery' && (
              <>
                <FormGroup>
                  <label>Adresse de livraison</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required={formData.deliveryOption === 'delivery'}
                    placeholder="Entrez votre adresse complète"
                  />
                </FormGroup>
                
                <FormRow>
                  <FormGroup>
                    <label>Ville</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required={formData.deliveryOption === 'delivery'}
                    />
                  </FormGroup>
                    
                  <FormGroup>
                    <label>Code postal</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      required={formData.deliveryOption === 'delivery'}
                    />
                  </FormGroup>
                </FormRow>
              </>
            )}

            <PaymentMethod 
              selectedMethod={formData.paymentMethod}
              onMethodChange={(method) => setFormData({...formData, paymentMethod: method})}
              customerInfo={formData}
              onCustomerInfoChange={(customerInfo) => setFormData({...formData, ...customerInfo})}
            />
          </motion.form>
        </FormSection>

        <OrderSummary>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 style={{ marginBottom: '1.5rem', color: '#1f2937' }}>
              Résumé de la commande
            </h2>
            
            {orderItems.map((item, index) => (
              <ProductItem key={index}>
                <div className="product-image">{item.emoji}</div>
                <div className="product-details">
                  <h4>{item.name}</h4>
                  <div className="config">{item.storage} • {item.color}</div>
                  <div className="quantity">Quantité: {item.quantity}</div>
                </div>
                <div className="product-price">
                  {item.totalPrice.toLocaleString()} FCFA
                </div>
              </ProductItem>
            ))}
            
            <SummaryRow>
              <span>Sous-total</span>
              <span>{calculateSubtotal().toLocaleString()} FCFA</span>
            </SummaryRow>
            
            <SummaryRow>
              <span>Livraison</span>
              <span>
                {calculateShipping() === 0 ? 'GRATUITE' : `${calculateShipping().toLocaleString()} FCFA`}
              </span>
            </SummaryRow>
            
            <SummaryRow className="total">
              <span>Total</span>
              <span>{calculateTotal().toLocaleString()} FCFA</span>
            </SummaryRow>
            
            <div style={{ marginTop: '1.5rem' }}>
              <FeatureBadge>
                <Truck size={14} />
                Livraison gratuite dès 500k FCFA
              </FeatureBadge>
              
              <FeatureBadge>
                <Shield size={14} />
                Garantie 2 ans officielle
              </FeatureBadge>
              
              <FeatureBadge>
                <Check size={14} />
                Produits 100% authentiques
              </FeatureBadge>
            </div>
            
            <SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Traitement en cours...' : 'Confirmer la commande'}
            </SubmitButton>
          </motion.div>
        </OrderSummary>
        </CheckoutGrid>
      </Container>
    </Section>
  );
};

export default CheckoutPage;
